"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { ArrowRight, Calendar, User } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
function FeaturedNewsCarouselConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [api, setApi] = useState<CarouselApi>();
  const [progress, setProgress] = useState(0);
  const AUTOPLAY_DURATION = 6000;
  const TICK_RATE = 50;
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onSelect = useCallback(() => {
    setProgress(0);
  }, []);
  useEffect(() => {
    if (!api) return;
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api, onSelect]);
  useEffect(() => {
    if (!api || loading || posts.length === 0) return;
    const startProgress = () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const increment = TICK_RATE / AUTOPLAY_DURATION * 100;
          const nextProgress = prev + increment;
          if (nextProgress >= 100) {
            api.scrollNext();
            return 0;
          }
          return nextProgress;
        });
      }, TICK_RATE);
    };
    startProgress();
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [api, loading, posts.length]);
  if (loading) {
    return <div className="w-full h-[60vh] min-h-[400px] bg-muted animate-pulse flex items-center justify-center">
        <span className="sr-only">Loading featured news...</span>
      </div>;
  }
  if (error) {
    return <div className="w-full py-20 text-center bg-muted/30">
        <p className="text-muted-foreground">Unable to load featured news.</p>
      </div>;
  }
  if (posts.length === 0) {
    return null;
  }
  return <div className="relative group w-full">
      <Carousel setApi={setApi} opts={{
      loop: true,
      align: "start"
    }} className="w-full">
        <CarouselContent>
          {posts.map((post, i) => <CarouselItem key={post.id} data-index={i} className="pl-0">
              <div className="relative w-full">
                <AspectRatio ratio={16 / 9} className="bg-muted">
                  <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569116/img/construction_project_placeholder.png"} alt={post.title} className="h-full w-full object-cover" />
                  {}
                  <div className="absolute inset-0 bg-black/10" />
                </AspectRatio>

                {}
                <div className="absolute bottom-0 left-0 w-full p-6 md:p-12 lg:p-16 flex flex-col justify-end items-start pointer-events-none">
                  <div className="bg-background/90 backdrop-blur-md border border-border/50 p-6 md:p-8 max-w-3xl shadow-lg pointer-events-auto">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                      {post.date && <span className="flex items-center gap-1.5">
                          <SafeHydrate><Calendar className="h-4 w-4 text-chart-2" /></SafeHydrate>
                          {new Date(post.date).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric"
                    })}
                        </span>}
                      {post.author && <span className="flex items-center gap-1.5">
                          <User className="h-4 w-4 text-chart-2" />
                          {post.author.name}
                        </span>}
                    </div>

                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-4 leading-tight tracking-tight">
                      <Link to={post.link || "#"} className="hover:text-chart-2 transition-colors">
                        {post.title}
                      </Link>
                    </h2>

                    <div className="text-muted-foreground line-clamp-2 mb-6 text-base md:text-lg" dangerouslySetInnerHTML={{
                  __html: post.excerpt || ""
                }} />

                    <Link to={post.link || "#"}>
                      <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none">
                        Read Full Story <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CarouselItem>)}
        </CarouselContent>

        {}
        <div className="hidden md:block">
          <CarouselPrevious className="left-4 bg-background/80 hover:bg-background border-none text-primary" />
          <CarouselNext className="right-4 bg-background/80 hover:bg-background border-none text-primary" />
        </div>
      </Carousel>

      {}
      <div className="absolute bottom-0 left-0 w-full h-1.5 bg-background/20 z-10">
        <Progress value={progress} className="h-full rounded-none bg-transparent [&>[data-slot=progress-indicator]]:bg-chart-2 [&>[data-slot=progress-indicator]]:transition-all [&>[data-slot=progress-indicator]]:duration-100 [&>[data-slot=progress-indicator]]:ease-linear" />
      </div>
    </div>;
}
export default function FeaturedNewsCarousel() {
  const wp_query_params = {
    per_page: 5,
    orderby: "date",
    order: "desc",
    ignore_sticky: false
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="featured-news-carousel" className="w-full bg-background">
      <PostsProvider wp_query={{
      per_page: 5,
      orderby: "date",
      order: "desc",
      ignore_sticky: false
    }} className="w-full">
        <FeaturedNewsCarouselConsumer />
      </PostsProvider>
    </section>;
}